// src/pages/Creditos.jsx
import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from '../firebaseconfig'
import { useAuth } from "../context/AuthContext";
import CreditDetailsModal from "../components/modal/CreditoDetallesModal"; // <-- ajustá la ruta si es necesario
import "../styles/solicitudesPages.css";
import PDFCreditosExporter from "../components/reports/PDFCreditosExporter"
import { Home, User, Car, Eye, AlertTriangle, FileText } from "lucide-react";

const CreditosPage = () => {
  const { user } = useAuth();

  const [creditos, setCreditos] = useState([]);
  const [filteredCreditos, setFilteredCreditos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  // menú acciones
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [actionMenuCoords, setActionMenuCoords] = useState({ top: 0, left: 0 });

  // modal detalles
  const [detalleCredito, setDetalleCredito] = useState(null);

  // rol/email
  const [rol, setRol] = useState("");
  const [emailLogueado, setEmailLogueado] = useState("");

  // ---- helpers
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 0,
    }).format(Number(amount || 0));

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("es-AR");
    } catch {
      return "N/A";
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      aprobada: { class: "badge-aprobada", text: "Aprobada" },
      pendiente: { class: "badge-pendiente", text: "Pendiente" },
      rechazada: { class: "badge-rechazada", text: "Rechazada" },
      revision: { class: "badge-revision", text: "En revisión" },
      faltaPago: { class: "badge-falta-pago", text: "Falta de pago" },
    };
    const badge = badges[estado] || { class: "badge-pendiente", text: estado || "Pendiente" };
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  // ---- carga rol + data
  useEffect(() => {
    const cargar = async () => {
      if (!user) return;
      setLoading(true);
      try {
        // 1) rol + email
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        const _rol = userDocSnap.exists()
          ? (userDocSnap.data().rol || "").toLowerCase()
          : "";
        const _email = (user.email || "").toLowerCase();
        setRol(_rol);
        setEmailLogueado(_email);

        // 2) traer créditos aprobados de las 3 colecciones
        const fuentes = [
          { col: "creditosHipotecarios", tipo: "hipotecario" },
          { col: "creditosPersonales", tipo: "personal" },
          { col: "creditosPrendarios", tipo: "prendario" },
        ];

        const acumulado = [];

        for (const f of fuentes) {
          // siempre solo "aprobada"
          let qBase = query(collection(db, f.col), where("estado", "==", "aprobada"));

          // si NO es admin, además filtra por email
          if (_rol !== "admin") {
            qBase = query(
              collection(db, f.col),
              where("estado", "==", "aprobada"),
              where("email", "==", _email)
            );
          }

          const snap = await getDocs(qBase);
          snap.forEach((d) => {
            const data = d.data();
            acumulado.push({
              id: d.id,
              tipo: f.tipo,
              // campos base esperados
              nombre: data.nombre || "",
              dni: data.dni || "",
              email: data.email || "",
              telefono: data.telefono || "",
              montoSolicitado: Number(data.montoSolicitado) || 0,
              plazo: Number(data.plazo) || 0,
              ingresos: Number(data.ingresos) || 0,
              observaciones: data.observaciones || "",
              estado: data.estado || "aprobada",
              fechaCreacion: data.fechaCreacion || new Date().toISOString(),
              motivoRechazo: data.motivoRechazo || "",
              // específicos
              tipoPropiedad: data.tipoPropiedad,
              ubicacion: data.ubicacion,
              valorPropiedad: data.valorPropiedad,
              destino: data.destino,
              situacionLaboral: data.situacionLaboral,
              marca: data.marca,
              modelo: data.modelo,
              año: data.año,
              tipoVehiculo: data.tipoVehiculo,
              version: data.version,
              // por si el modal necesita más
              ...data,
            });
          });
        }

        // ordenar por fecha desc
        acumulado.sort((a, b) => new Date(b.fechaCreacion) - new Date(a.fechaCreacion));

        setCreditos(acumulado);
        setFilteredCreditos(acumulado);
      } catch (e) {
        console.error("Error cargando créditos:", e);
        alert("Error al cargar los créditos: " + e.message);
      } finally {
        setLoading(false);
      }
    };

    cargar();
  }, [user]);

  // ---- filtro de búsqueda
  useEffect(() => {
    const s = (searchTerm || "").toLowerCase();
    const filtered = creditos.filter(
      (c) =>
        c.nombre.toLowerCase().includes(s) ||
        (c.dni || "").toString().includes(s) ||
        (c.id || "").toLowerCase().includes(s) ||
        (c.email || "").toLowerCase().includes(s)
    );
    setFilteredCreditos(filtered);
  }, [searchTerm, creditos]);

  // ---- actualizar estado (solo admin desde esta vista)
  const actualizarEstado = async (credito, nuevoEstado, extra = {}) => {
    try {
      const coleccion = `creditos${credito.tipo.charAt(0).toUpperCase()}${credito.tipo.slice(1)}s`;
      const ref = doc(db, coleccion, credito.id);
      await updateDoc(ref, {
        estado: nuevoEstado,
        fechaActualizacion: new Date().toISOString(),
        ...extra,
      });

      // actualizar local
      setCreditos((prev) =>
        prev.map((c) => (c.id === credito.id ? { ...c, estado: nuevoEstado, ...extra } : c))
      );
      setFilteredCreditos((prev) =>
        prev.map((c) => (c.id === credito.id ? { ...c, estado: nuevoEstado, ...extra } : c))
      );

      return true;
    } catch (e) {
      console.error("Error actualizando estado:", e);
      alert("No se pudo actualizar el estado. " + e.message);
      return false;
    }
  };

  const marcarFaltaDePago = async (credito) => {
    if (rol !== "admin") return;
    const conf = window.confirm(
      `¿Confirmás marcar como FALTA DE PAGO el crédito de ${credito.nombre}?\n\nEmail: ${credito.email}`
    );
    if (!conf) return;
    await actualizarEstado(credito, "faltaPago");
  };

  // ---- UI
  if (loading) {
    return (
      <div className="solicitudes-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando créditos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="solicitudes-page">
      <div className="page-header">
        <div className="header-actions">
          <h2 style={{ margin: 0 }}>Créditos</h2>
        </div>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Buscar por nombre, DNI, ID o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {filteredCreditos.length > 0 && (
          <span className="search-results">
            {filteredCreditos.length} crédito{filteredCreditos.length !== 1 ? "s" : ""} encontrado
            {filteredCreditos.length !== 1 ? "s" : ""}
          </span>
        )}
        {rol === "admin" && <PDFCreditosExporter data={filteredCreditos} />}
      </div>

      <div className="table-container">
        <table className="solicitudes-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Tipo</th>
              <th>Nombre</th>
              <th>DNI</th>
              <th>Email</th>
              <th>Monto</th>
              <th>Fecha</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {filteredCreditos.map((c) => (
              <tr key={c.id}>
                <td className="id-cell">{c.id.substring(0, 8)}...</td>
                <td>
                  <span className="tipo-badge tipo-prendario" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {c.tipo === "hipotecario" ? <><Home size={13} />Hipotecario</> : c.tipo === "personal" ? <><User size={13} />Personal</> : <><Car size={13} />Prendario</>}
                  </span>
                </td>
                <td className="nombre-cell">{c.nombre}</td>
                <td>{c.dni}</td>
                <td className="email-cell">{c.email}</td>
                <td className="monto-cell">{formatCurrency(c.montoSolicitado)}</td>
                <td>{formatDate(c.fechaCreacion)}</td>
                <td>{getEstadoBadge(c.estado)}</td>
                <td className="acciones-cell">
                  <div className="acciones-container">
                    <button
                      className="acciones-button"
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setActionMenuCoords({
                          top: rect.bottom,
                          left: Math.min(rect.left, window.innerWidth - 220),
                        });
                        setShowActionMenu(showActionMenu === c.id ? null : c.id);
                      }}
                    >
                      ⋯
                    </button>

                    {showActionMenu === c.id && (
                      <div
                        className="acciones-menu"
                        style={{
                          position: "fixed",
                          top: `${actionMenuCoords.top}px`,
                          left: `${actionMenuCoords.left}px`,
                          zIndex: 9999,
                          width: "220px",
                          maxWidth: "90vw",
                        }}
                      >
                        <button
                          onClick={() => {
                            setDetalleCredito(c);
                            setShowActionMenu(null);
                          }}
                        >
                          <Eye size={14} style={{ marginRight: 6 }} /> Ver detalles
                        </button>

                        {rol === "admin" && c.estado !== "faltaPago" && (
                          <button onClick={() => marcarFaltaDePago(c)} className="rechazar-btn">
                            <AlertTriangle size={14} style={{ marginRight: 6 }} /> Marcar falta de pago
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredCreditos.length === 0 && !loading && (
        <div className="empty-state">
          <div className="empty-icon"><FileText size={48} color="#9ca3af" /></div>
          <h3>No se encontraron créditos</h3>
          <p>No hay créditos que coincidan con tu búsqueda.</p>
        </div>
      )}

      {/* Modal nuevo de detalles */}
      {detalleCredito && (
        <CreditDetailsModal
          open={!!detalleCredito}
          onClose={() => setDetalleCredito(null)}
          credito={detalleCredito}
          isAdmin={rol === "admin"}
          onChangeStatus={async (nuevo, extra) => {
            // callback por si en el modal se cambia algo
            await actualizarEstado(detalleCredito, nuevo, extra);
          }}
          onGeneratePDF={() => {
            // opcional: si tu modal llama internamente, podés omitir esto
            // acá podrías disparar tu generador actual si preferís hacerlo desde el padre
          }}
        />
      )}
    </div>
  );
};

export default CreditosPage;
